import Axios from '../../axiosConfig';
import { BACKEND_URL } from '../../config';
import { configure, runInAction, makeAutoObservable } from 'mobx';
import CarouselImage from '../../types/carouselImage';

configure({ enforceActions: 'observed' });

export default class ImageModule {
    private activeImages: CarouselImage[] = [];
    private allImages: CarouselImage[] = [];

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public async fetchCarouselImages(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'imageManagement/carouselImages',
            withCredentials: true,
        });
        runInAction(() => (this.allImages = response.data));
    }

    public async fetchActiveCarouselImages(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'imageManagement/activeCarouselImages',
            withCredentials: true,
        });
        runInAction(() => (this.activeImages = response.data));
    }

    public async getAllCarouselImages(): Promise<void> {
        const response = await Axios({
            method: 'GET',
            url: BACKEND_URL + 'app/uploadCarouselImage',
        });
        runInAction(() => (this.allImages = response.data));
    }

    public async uploadCarouselImages(fileData: File, redirectURL: string): Promise<void> {
        const formData = new FormData();
        formData.append('file', fileData);
        formData.append('redirectUrl', redirectURL);

        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'imageManagement/uploadCarouselImage',
            data: formData,
            withCredentials: true,
        });
        const uploadedImage = response.data;
        runInAction(() => {
            this.allImages.push(uploadedImage);
        });
    }

    public async saveCarouselImage(requestData: CarouselImage): Promise<void> {
        const response = await Axios({
            method: 'POST',
            url: BACKEND_URL + 'imageManagement/updateCarouselImage',
            data: requestData,
            withCredentials: true,
        });
        const savedImage: CarouselImage = response.data;
        const allImagesIndex = this.allImages.findIndex((img) => img.id === savedImage.id);
        const activeImagesIndex = this.activeImages.findIndex((img) => img.id === savedImage.id);

        runInAction(() => {
            if (allImagesIndex > -1) this.allImages[allImagesIndex] = savedImage;

            if (activeImagesIndex > -1 && savedImage.active) this.activeImages[activeImagesIndex] = savedImage;
            else if (savedImage.active) this.activeImages.push(savedImage);
        });
    }

    public async deleteCarouselImage(id: number): Promise<void> {
        const response = await Axios({
            method: 'DELETE',
            url: BACKEND_URL + `imageManagement/deleteCarouselImage/${id}`,
            withCredentials: true,
        });
        if (response.status === 200) {
            // Remove deleted image from list and update order number for each remaining image.
            const updatedListOfCarouselImages = this.allImages
                .slice()
                .filter((img) => img.id !== id)
                .map((img, index) => ({ ...img, orderNumber: index + 1 }));

            runInAction(() => {
                this.allImages = updatedListOfCarouselImages;
                this.activeImages = updatedListOfCarouselImages.filter((img) => img.active);
            });
        }
    }

    public async uploadBackgroundImage(file: File): Promise<void> {
        const form = new FormData();
        form.append('file', file);

        await Axios({
            method: 'POST',
            url: BACKEND_URL + 'imageManagement/uploadBackgroundImage',
            data: form,
            withCredentials: true,
        });
    }

    public get activeCarouselImages(): CarouselImage[] {
        return this.activeImages;
    }

    public get allCarouselImages(): CarouselImage[] {
        return this.allImages;
    }
}
